import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { NotificationLiveDto } from '@core/api/notification-api.service';

/**
 * Real-time notifications hub at `{apiUrl}/notification`.
 * Mirrors the legacy Angular NotificationsSignalRService.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsSignalRService {
  private hubConnection: signalR.HubConnection | null = null;
  private starting: Promise<void> | null = null;
  private handlersBound = false;

  private readonly notificationSubject = new Subject<NotificationLiveDto>();
  readonly notifications$ = this.notificationSubject.asObservable();

  async ensureConnected(): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      return;
    }
    if (this.starting) {
      return this.starting;
    }

    if (!this.hubConnection) {
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${environment.apiUrl}/notification`, {
          transport: signalR.HttpTransportType.WebSockets,
          accessTokenFactory: () => localStorage.getItem('token') || '',
        })
        .withAutomaticReconnect()
        .build();
      this.bindHandlers();
    }

    this.starting = this.hubConnection
      .start()
      .then(() => undefined)
      .catch((err) => {
        console.error('Notifications SignalR connection failed', err);
        throw err;
      })
      .finally(() => {
        this.starting = null;
      });

    return this.starting;
  }

  /** Disconnects the authenticated session so the next login joins its own group. */
  async stop(): Promise<void> {
    const connection = this.hubConnection;
    if (!connection) return;

    // Detach the shared reference first: a later login can build a fresh
    // connection even if the old socket takes a moment to close.
    this.hubConnection = null;
    this.handlersBound = false;

    try {
      await this.starting;
    } catch {
      // A failed initial connection is already disconnected.
    } finally {
      this.starting = null;
    }

    if (connection.state !== signalR.HubConnectionState.Disconnected) {
      await connection.stop();
    }
  }

  private bindHandlers(): void {
    if (!this.hubConnection || this.handlersBound) return;
    this.handlersBound = true;

    this.hubConnection.on('ReceiveNotification', (message: NotificationLiveDto) => {
      this.notificationSubject.next(message);
    });
  }
}
